import { useCallback, useMemo } from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Form, FormRadioGroup, FormRadioButton } from "@jsq/ds/forms";
import { useFeatureFlag } from "@jsq/flags-react";
import { client as apolloClient } from "~/apollo/client";
import {
  useGetSafeCaseIdsForClientQuery,
  useSearchFundFamiliesQuery,
} from "~/graphql/supergraphHooks";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { SPACING } from "~/styles/variables";
import { ConditionalFields } from "./ConditionalFields";
import type { BillingGroupFormValues, BillingGroupModalProps } from "./types";

const initialValues: BillingGroupFormValues = {
  assignmentType: "existing",
  fundFamily: null,
  fundFamilyName: "",
  safeCaseId: null,
};

export const BillingGroupModal = ({
  mode,
  isOpen,
  onClose,
  onSubmit,
  selectedEntityCount,
  clientId,
}: BillingGroupModalProps) => {
  const isSafeCaseIdEnabled = useFeatureFlag("billingSafeCaseIdAndActiveInvestments", false);
  const { asOfDateString } = useBillingDate();

  const { data, loading } = useSearchFundFamiliesQuery({
    variables: { clientId },
    skip: !isOpen,
    client: apolloClient,
    fetchPolicy: "network-only",
  });

  const { data: safeCaseIdsData, loading: safeCaseIdsLoading } =
    useGetSafeCaseIdsForClientQuery({
      variables: { clientId: String(clientId), asOfDate: asOfDateString },
      skip: !isOpen || !isSafeCaseIdEnabled,
      client: apolloClient,
      fetchPolicy: "network-only",
    });

  const families = useMemo(
    () => data?.searchFundFamilies?.families ?? [],
    [data?.searchFundFamilies?.families],
  );

  const safeCaseIdOptions = useMemo(
    () => safeCaseIdsData?.getSafeCaseIdsForClient?.safeCaseIdOptions ?? [],
    [safeCaseIdsData?.getSafeCaseIdsForClient?.safeCaseIdOptions],
  );

  const handleSubmit = useCallback(
    async (values: BillingGroupFormValues) => {
      if (values.assignmentType === "new") {
        const trimmed = values.fundFamilyName.trim();
        if (!trimmed) {
          return;
        }
        await onSubmit({ ...values, fundFamilyName: trimmed }, trimmed);
        return;
      }

      if (!values.fundFamily) {
        return;
      }
      const match = families.find((f) => f.id === values.fundFamily);
      await onSubmit(values, match?.displayName ?? "");
    },
    [onSubmit, families],
  );

  if (!isOpen) {
    return null;
  }

  const entityLabel = selectedEntityCount === 1 ? "entity" : "entities";
  const isReassign = mode === "reassign";
  const suffix = isSafeCaseIdEnabled ? " and safe case id" : "";
  const title = isReassign
    ? `Re-assign to billing group${suffix}`
    : `Assign to billing group${suffix}`;
  const modalTestId = isReassign
    ? "reassign-billing-group-modal"
    : "assign-billing-group-modal";
  const radioGroupTestId = isReassign
    ? "reassignment-type-radio-group"
    : "assignment-type-radio-group";
  const existingLabel = isReassign
    ? `Re-assign to existing billing group${suffix}`
    : `Assign to existing billing group${suffix}`;
  const newLabel = isReassign
    ? `Re-assign to new billing group${suffix}`
    : `Assign to new billing group${suffix}`;

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title={title}
      description={`${selectedEntityCount} selected ${entityLabel}`}
      data-testid={modalTestId}
    >
      <Form<BillingGroupFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <FormRadioGroup name="assignmentType" data-testid={radioGroupTestId}>
              <Flex flexDirection="column" gap={SPACING.SMALL}>
                <FormRadioButton value="existing">
                  <Text variant="regular-md">{existingLabel}</Text>
                </FormRadioButton>
                <FormRadioButton value="new">
                  <Text variant="regular-md">{newLabel}</Text>
                </FormRadioButton>
              </Flex>
            </FormRadioGroup>
            <ConditionalFields
              families={families}
              loading={loading}
              safeCaseIdOptions={safeCaseIdOptions}
              safeCaseIdsLoading={safeCaseIdsLoading}
              isSafeCaseIdEnabled={isSafeCaseIdEnabled}
            />
          </Flex>
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Submit for approval
            </Button>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
